import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiPhone, FiMail, FiMapPin, FiCalendar, FiClock, FiHeart, FiShield, FiUsers, FiHome, FiCheckCircle, FiArrowRight } = FiIcons;

const RequestCare = () => {
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    // Contact Information
    phone: '',
    email: '',
    address: '',
    city: '',
    postcode: '',
    emergencyContact: '',
    emergencyPhone: '',
    // Care Requirements
    careType: '',
    careLevel: '',
    startDate: '',
    preferredTime: '',
    duration: '',
    // Health Information
    medicalConditions: '',
    medications: '',
    mobility: '',
    cognitiveStatus: '',
    // Additional Information
    previousCare: '',
    specialRequirements: '',
    additionalInfo: '',
    // Preferences
    genderPreference: '',
    languagePreference: '',
    petFriendly: false,
    // Consent
    dataConsent: false,
    marketingConsent: false
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would normally send the data to your backend
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
  };

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    { number: 1, title: 'Personal Details', icon: FiUser },
    { number: 2, title: 'Care Requirements', icon: FiHeart },
    { number: 3, title: 'Health Information', icon: FiShield },
    { number: 4, title: 'Preferences', icon: FiUsers },
    { number: 5, title: 'Review & Submit', icon: FiCheckCircle }
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8 text-center border border-primary-100"
        >
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-primary-900" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Request Submitted Successfully!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your care request. Our team will review your information and contact you within 24 hours to discuss your care needs and arrange a consultation.
          </p>
          <div className="bg-primary-50 p-4 rounded-lg mb-6 border border-primary-100">
            <p className="text-sm text-primary-800">
              <strong>What happens next?</strong><br />
              1. Care assessment call within 24 hours<br />
              2. In-home consultation if needed<br />
              3. Personalized care plan creation<br />
              4. Care team introduction and start
            </p>
          </div>
          <p className="text-sm text-gray-600">
            Need immediate assistance? Call our 24/7 helpline: <strong>03333 394 077</strong>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Request Care - Odara Care | Professional Home Care Services</title>
        <meta name="description" content="Request professional home care services from Odara Care. Complete our comprehensive care assessment form to get personalized care solutions." />
        <meta name="keywords" content="request care, home care assessment, care consultation, elderly care, personal care services" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Request Care Services</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete this comprehensive assessment to help us understand your care needs and provide the best possible service.
            </p>
          </motion.div>

          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-8">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center">
                  <div className={`flex items-center justify-center w-12 h-12 rounded-full ${
                    currentStep >= step.number 
                      ? 'bg-primary-900 text-white' 
                      : 'bg-white text-gray-600 border-2 border-gray-200'
                  }`}>
                    <SafeIcon icon={step.icon} className="w-6 h-6" />
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <div className={`text-sm font-medium ${
                      currentStep >= step.number ? 'text-primary-900' : 'text-gray-500'
                    }`}>
                      Step {step.number}
                    </div>
                    <div className="text-sm text-gray-600">{step.title}</div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`hidden sm:block w-16 h-1 mx-4 ${
                      currentStep > step.number ? 'bg-primary-900' : 'bg-gray-200'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl shadow-lg p-8 border border-primary-100"
          >
            <form onSubmit={handleSubmit}>
              {/* Step 1: Personal Details */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6">Personal Details</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="prefer-not-to-say">Prefer not to say</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Home Address *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Postcode *</label>
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact Name</label>
                      <input
                        type="text"
                        name="emergencyContact"
                        value={formData.emergencyContact}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact Phone</label>
                      <input
                        type="tel"
                        name="emergencyPhone"
                        value={formData.emergencyPhone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Care Requirements */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6">Care Requirements</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type of Care Needed *</label>
                    <select
                      name="careType"
                      value={formData.careType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select Care Type</option>
                      <option value="personal-care">Personal Care</option>
                      <option value="companionship">Companionship</option>
                      <option value="dementia-care">Dementia Care</option>
                      <option value="live-in-care">Live-in Care</option>
                      <option value="respite-care">Respite Care</option>
                      <option value="palliative-care">Palliative Care</option>
                      <option value="post-hospital-care">Post-Hospital Care</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Level of Care Required *</label>
                    <select
                      name="careLevel"
                      value={formData.careLevel}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select Care Level</option>
                      <option value="low">Low - Minimal assistance with daily activities</option>
                      <option value="medium">Medium - Regular assistance with personal care</option>
                      <option value="high">High - Extensive care and supervision needed</option>
                      <option value="complex">Complex - Specialized medical care required</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Start Date *</label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time</label>
                      <select
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select Time</option>
                        <option value="morning">Morning (8am - 12pm)</option>
                        <option value="afternoon">Afternoon (12pm - 6pm)</option>
                        <option value="evening">Evening (6pm - 10pm)</option>
                        <option value="overnight">Overnight (10pm - 8am)</option>
                        <option value="24-hour">24-hour care</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration of Care *</label>
                    <select
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select Duration</option>
                      <option value="short-term">Short-term (less than 3 months)</option>
                      <option value="medium-term">Medium-term (3-12 months)</option>
                      <option value="long-term">Long-term (over 12 months)</option>
                      <option value="ongoing">Ongoing/Indefinite</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Step 3: Health Information */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6">Health Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Medical Conditions</label>
                    <textarea
                      name="medicalConditions"
                      value={formData.medicalConditions}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Please list any medical conditions, diagnoses, or health concerns..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Medications</label>
                    <textarea
                      name="medications"
                      value={formData.medications}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Please list all current medications, dosages, and frequency..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mobility Level *</label>
                    <select
                      name="mobility"
                      value={formData.mobility}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select Mobility Level</option>
                      <option value="fully-mobile">Fully mobile - no assistance needed</option>
                      <option value="walking-aid">Mobile with walking aid (stick, frame, etc.)</option>
                      <option value="limited-mobility">Limited mobility - some assistance needed</option>
                      <option value="wheelchair-user">Wheelchair user</option>
                      <option value="bed-bound">Bed-bound or very limited mobility</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cognitive Status *</label>
                    <select
                      name="cognitiveStatus"
                      value={formData.cognitiveStatus}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select Cognitive Status</option>
                      <option value="no-concerns">No cognitive concerns</option>
                      <option value="mild-concerns">Mild memory/cognitive concerns</option>
                      <option value="moderate-dementia">Moderate dementia/cognitive impairment</option>
                      <option value="severe-dementia">Severe dementia/cognitive impairment</option>
                      <option value="other-cognitive">Other cognitive condition</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Previous Care Experience</label>
                    <textarea
                      name="previousCare"
                      value={formData.previousCare}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Please describe any previous care services used..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Preferences */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6">Care Preferences</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Carer Gender Preference</label>
                    <select
                      name="genderPreference"
                      value={formData.genderPreference}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">No preference</option>
                      <option value="male">Male carer preferred</option>
                      <option value="female">Female carer preferred</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Language Preference</label>
                    <input
                      type="text"
                      name="languagePreference"
                      value={formData.languagePreference}
                      onChange={handleInputChange}
                      placeholder="e.g., English, Welsh, Spanish, etc."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="petFriendly"
                      checked={formData.petFriendly}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label className="ml-2 text-sm text-gray-700">
                      Pet-friendly carer required (we have pets in the home)
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Special Requirements</label>
                    <textarea
                      name="specialRequirements"
                      value={formData.specialRequirements}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Any special requirements, cultural considerations, dietary needs, etc..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                    <textarea
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Any other information you'd like us to know..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Step 5: Review & Submit */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6">Review & Submit</h3>
                  
                  <div className="bg-primary-50 p-6 rounded-lg space-y-4 border border-primary-100">
                    <h4 className="font-semibold text-gray-900">Request Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Name:</span> {formData.firstName} {formData.lastName}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span> {formData.phone}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {formData.email}
                      </div>
                      <div>
                        <span className="font-medium">Care Type:</span> {formData.careType}
                      </div>
                      <div>
                        <span className="font-medium">Care Level:</span> {formData.careLevel}
                      </div>
                      <div>
                        <span className="font-medium">Start Date:</span> {formData.startDate}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        name="dataConsent"
                        checked={formData.dataConsent}
                        onChange={handleInputChange}
                        required
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-1"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        I consent to Odara Care processing my personal data for the purpose of care assessment and service provision. *
                      </label>
                    </div>
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        name="marketingConsent"
                        checked={formData.marketingConsent}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-1"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        I would like to receive updates about Odara Care services and health-related information.
                      </label>
                    </div>
                  </div>

                  <div className="bg-primary-50 p-4 rounded-lg border border-primary-100">
                    <h5 className="font-medium text-primary-900 mb-2">What happens next?</h5>
                    <ul className="text-sm text-primary-800 space-y-1">
                      <li>• We'll call you within 24 hours to discuss your needs</li>
                      <li>• A care coordinator will arrange a home assessment if needed</li>
                      <li>• We'll create a personalized care plan for you</li>
                      <li>• Care can typically start within 48-72 hours</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-primary-100">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    currentStep === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Previous
                </button>
                
                {currentStep < 5 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="bg-primary-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-800 transition-colors flex items-center"
                  >
                    Next Step
                    <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!formData.dataConsent}
                    className={`px-8 py-3 rounded-lg font-medium transition-colors flex items-center ${
                      formData.dataConsent
                        ? 'bg-primary-900 text-white hover:bg-primary-800'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Submit Request
                    <SafeIcon icon={FiCheckCircle} className="ml-2 w-4 h-4" />
                  </button>
                )}
              </div>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-12 bg-gradient-to-r from-primary-900 to-secondary-900 text-white rounded-xl p-8 text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Need Help Completing This Form?</h3>
            <p className="text-primary-100 mb-6">
              Our care coordinators are available 24/7 to assist you with your care request.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:03333394077"
                className="inline-flex items-center justify-center bg-white text-primary-900 px-6 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
              >
                <SafeIcon icon={FiPhone} className="mr-2 w-5 h-5" />
                Call 03333 394 077
              </a>
              <a
                href="mailto:enquiries@odaracare.org.uk"
                className="inline-flex items-center justify-center border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-900 transition-colors"
              >
                <SafeIcon icon={FiMail} className="mr-2 w-5 h-5" />
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default RequestCare;